import React, { useMemo } from 'react';
import { useBusinessContext } from '../contexts/BusinessContext';
import { motivationalMessages } from '../data/staticData';
import { usePricing } from '../contexts/PricingContext';
import './PricingPlansDialog.css';

export function PricingPlansDialog() {
  const { 
    isPricingDialogOpen, 
    closePricingDialog, 
    businessDetails,
    openCountryDetailsDialog,
    openBusinessSetupDialog,
    setBusinessDetails,
    setCurrentMessage
  } = useBusinessContext();

  const pricing = usePricing();
  const plans = useMemo(() => pricing?.plans ?? [], [pricing]);

  const handleBackToCountryDetails = () => {
    closePricingDialog();
    openCountryDetailsDialog();
  };

  const handleBuyNow = (plan: typeof plans[number]) => {
    setBusinessDetails({
      ...businessDetails!,
      selectedPlan: plan.name,
      selectedPlanId: plan.id,
      selectedPricingPlan: plan
    });
    
    setCurrentMessage(motivationalMessages.planSelected);
    closePricingDialog();
    openBusinessSetupDialog();
  };

  if (!isPricingDialogOpen || !businessDetails) return null;

  const formatPrice = (plan: typeof plans[number]) => {
    if (plan?.priceDisplay) {
      return plan.priceDisplay;
    }
    if (plan?.setupFee?.amount) {
      return plan.setupFee.amount;
    }
    if (plan?.subscription?.amount) {
      return plan.subscription.amount;
    }
    return '';
  };

  return (
    <div className="pricing-plans-overlay" onClick={closePricingDialog}>
      <div className="pricing-plans-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="pricing-plans-header">
          <button className="pricing-plans-back" onClick={handleBackToCountryDetails}>
            ←
          </button>
          <button className="pricing-plans-close" onClick={closePricingDialog}>
            ×
          </button>
        </div>

        <div className="pricing-plans-content">
          <div className="pricing-plans-title-section">
            <h2 className="pricing-plans-title">Choose Your Plan</h2>
            <p className="pricing-plans-subtitle">
              Select the perfect plan for your business in {businessDetails.country}
            </p>
          </div>

          {plans.length === 0 ? (
            <div className="pricing-plans-empty">
              No pricing plans available. Please add plans in the Pricing section of the admin panel.
            </div>
          ) : (
            <div className="pricing-plans-grid">
              {plans.map((plan) => (
                <div key={plan.id} className={`pricing-plan-card ${plan.badge ? 'pricing-plan-popular' : ''}`}>
                  {plan.badge && (
                    <div className="pricing-plan-badge">{plan.badge}</div>
                  )}
                  <div className="pricing-plan-header">
                    <h3 className="pricing-plan-name">{plan.name}</h3>
                  </div>
                  <div className="pricing-plan-body">
                    <div className="pricing-plan-price">
                      <span className="pricing-plan-amount">{formatPrice(plan)}</span>
                    </div>
                    {plan.description && <p className="pricing-plan-description">{plan.description}</p>}
                    <div className="pricing-plan-features">
                      {(plan.features || []).map((feature, idx) => (
                        <div key={idx} className="pricing-plan-feature">
                          <span className="pricing-plan-check">✓</span>
                          <span className="pricing-plan-feature-text">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <button
                      className={`pricing-plan-button ${plan.badge ? 'pricing-plan-button-popular' : ''}`}
                      onClick={() => handleBuyNow(plan)}
                    >
                      {plan.button || 'Choose Plan'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

